import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const FounderPage = () => {
  return (
    <div className="flex-1 bg-[#FAF8F3]" data-testid="founder-page">
      {/* Header */}
      <section className="bg-primary-dark text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Link to="/about">
            <Button variant="ghost" className="text-white hover:bg-white/10 mb-4">
              <ArrowLeft className="mr-2" size={16} />
              Back to About
            </Button>
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 font-heading">
            About Our Founder
          </h1>
          <p className="text-lg text-gray-200 max-w-3xl">
            Meet Dominic Clichee, MPH, DrPH(c)
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="shadow-xl">
            <CardContent className="p-8 md:p-12">
              {/* Title */}
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-neutral-charcoal mb-2 font-heading">
                  Dominic Clichee, MPH, DrPH(c)
                </h2>
                <p className="text-lg text-primary font-semibold">
                  Founder, Population, Community, Public Health Services Center
                </p>
              </div>

              {/* Introduction */}
              <div className="prose prose-lg max-w-none mb-8">
                <p className="text-gray-700 leading-relaxed mb-6">
                  Dominic Clichee is a public health strategist and epidemiologist with more than a decade of experience strengthening community health systems across Tribal, rural, and underserved regions. His work integrates epidemiology, program design, community engagement, and data-driven decision-making to help health departments build sustainable, high-impact services for their communities.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Dominic has led major public health initiatives at the hospital, Tribal, and state levels—designing programs, improving surveillance systems, securing funding, and building teams that deliver measurable improvements in health outcomes.
                </p>
              </div>

              {/* Why Organizations Partner */}
              <div className="mb-10">
                <h3 className="text-2xl font-bold text-neutral-charcoal mb-6 font-heading">
                  Why Organizations Partner With Dominic
                </h3>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-semibold text-primary mb-3 font-heading">
                      Proven Community Health Leadership
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      Dominic previously served as Chief Community Health Officer, where he oversaw nursing, nutrition, chronic disease management, mobile health, wellness, and food security programs. His strategic planning and operational leadership increased program engagement by 25% and elevated community health as a core pillar of hospital operations.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-primary mb-3 font-heading">
                      Expert Epidemiological & Data Systems Support
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      He has directed infectious and chronic disease surveillance across multiple facilities, improving outbreak detection, enhancing data accuracy, and developing predictive analytics used by clinical departments. Dominic translates complex data into practical action plans that improve public health performance immediately.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-primary mb-3 font-heading">
                      Program Design, Evaluation & Expansion
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      From producing community health needs assessments to building new programs such as a hospital-based produce prescription initiative, Dominic specializes in helping organizations launch, strengthen, and evaluate public health services. He has evaluated CDC-funded statewide health equity programs and ensured compliance across large, multi-site initiatives.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-primary mb-3 font-heading">
                      Funding, Grant Management & Sustainability
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      Dominic has managed $1M+ budgets, secured multi-year funding, and obtained over $500,000 in additional resources for chronic disease and behavioral health programs. He helps organizations structure budgets, strengthen proposals, and build long-term sustainability.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-primary mb-3 font-heading">
                      Tribal & Rural Health Expertise
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      As a Navajo public health professional with extensive experience across Tribal nations, he brings deep cultural grounding, community-specific insight, and a practical understanding of health system challenges in remote settings.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-primary mb-3 font-heading">
                      Workforce Development & Training
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      Dominic has trained epidemiologists, public health practitioners, graduate students, CHWs, and cross-functional teams. His capacity-building approach strengthens internal expertise so organizations can continue advancing long after the project ends.
                    </p>
                  </div>
                </div>
              </div>

              {/* Consulting Services */}
              <div className="mb-10">
                <h3 className="text-2xl font-bold text-neutral-charcoal mb-6 font-heading">
                  Consulting Services
                </h3>
                <p className="text-gray-700 mb-4">Dominic provides tailored consulting in:</p>
                <ul className="space-y-2">
                  {[
                    "Community Health Strategy & Program Development",
                    "Epidemiology & Data System Design",
                    "Public Health Evaluation, Dashboards & KPIs",
                    "Community Health Needs Assessments (IRS/ACA-compliant)",
                    "Grant Writing, Budget Structuring & Program Sustainability",
                    "Health Equity, SDOH Integration & Intervention Design",
                    "Mobile Health & Outreach Program Planning",
                    "Workforce Training & Professional Development"
                  ].map((service, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <span className="text-primary mt-1 font-bold">•</span>
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Education */}
              <div className="border-t border-gray-200 pt-8">
                <h3 className="text-2xl font-bold text-neutral-charcoal mb-6 font-heading">
                  Education
                </h3>
                <ul className="space-y-3">
                  <li className="text-gray-700">
                    <span className="font-semibold">Doctor of Public Health (DrPH), Candidate</span> – University of Arizona
                  </li>
                  <li className="text-gray-700">
                    <span className="font-semibold">Master of Public Health (MPH)</span> – University of Arizona
                  </li>
                  <li className="text-gray-700">
                    <span className="font-semibold">Bachelor of Science, Business Administration</span>
                  </li>
                  <li className="text-gray-700">
                    <span className="font-semibold">Bachelor of Science, Tribal Management</span> (Completed coursework)
                  </li>
                </ul>
              </div>

              {/* CTA */}
              <div className="mt-10 pt-8 border-t border-gray-200 text-center">
                <p className="text-gray-700 mb-6">
                  Interested in partnering with Dominic to strengthen your community health systems?
                </p>
                <Link to="/contact">
                  <Button size="lg" className="bg-primary hover:bg-primary-dark text-white">
                    Get In Touch
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default FounderPage;
